import React, { useState, forwardRef, useImperativeHandle, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import fileDownload from 'js-file-download'
import { TextField, Grid, Button, Tooltip, LinearProgress, Typography } from '@mui/material'

import { colorTypes, textFieldTypes, variantTypes } from '../../../../constants'
import { Logger, LoggerTypes, removeSpace } from '../../../../utils'
import { FormTitle } from '../../../forms'
import { Icon, iconNames } from '../../../icons'

const defaultValues = {
  id: null,
  name: '',
  wind_10y_RP: 0,
  filename: null
}

export const AttributeWeatherForm = forwardRef(
  ({ setOpenDialogWeather, title, weather, dispatchAction, errors, _progress, _isSuccess }, ref) => {
    const { t } = useTranslation()
    const inputRef = useRef(null)

    const [id] = useState(weather.id || defaultValues.id)
    const [name, setName] = useState(weather.name || defaultValues.name)
    const [wind_10y_RP, setWind10yRP] = useState(weather.wind_10y_RP || defaultValues.wind_10y_RP)

    // -- File to upload
    const [filename, setFilename] = useState(weather.filename || defaultValues.filename)
    const [selectedFile, setSelectedFile] = useState()
    const [progress] = useState(_progress)
    const [isSuccess] = useState(_isSuccess)

    const onChangeName = (e) => {
      e.stopPropagation()
      const _value = e.target.value
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeWeatherForm.onChangeWind10yRP()', { name: _value })
      setName(removeSpace(_value))
    }

    const onChangeWind10yRP = (e) => {
      e.stopPropagation()
      const _value = e.target.value
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeWeatherForm.onChangeWind10yRP()', { wind_10y_RP: _value })
      setWind10yRP(_value)
    }

    const onSelectFile = (e) => {
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeWeatherForm.onSelectFile()', e)

      const _selectedFile = e?.target?.files[0] || null

      setSelectedFile(_selectedFile)
      setFilename(_selectedFile.name)
    }

    const onExcelSampleClick = (e) => {
      e.stopPropagation()

      const _filename = 'WeatherDataSample.csv'
      const url = `${window.location.protocol}//${window.location.host}/${_filename}`

      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeWeatherForm.onExcelSampleClick()', { url })

      axios
        .get(url, {
          responseType: 'blob'
        })
        .then((res) => {
          fileDownload(res.data, _filename)
        })
    }

    useEffect(() => {
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeWeatherForm.useEffect()', { weather })
      inputRef.current.focus()
    }, [weather])

    // ---

    const onCloseDialog = () => {
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeWeatherForm.onCloseDialog()')
      setOpenDialogWeather(false)
    }

    useImperativeHandle(ref, () => ({
      async onSubmitForm() {
        const newWeather = new FormData()
        if (name !== '') {
          newWeather.append('name', name)
        }
        if (wind_10y_RP !== '') {
          newWeather.append('wind_10y_RP', wind_10y_RP)
        }
        if (selectedFile) {
          newWeather.append('data', selectedFile)
        }

        Logger.trace(LoggerTypes.CONTROLLER, 'AttributeWeatherForm.onSubmitForm()', {
          newWeather,
          weather
        })

        const _onSuccess = () => {
          Logger.trace(LoggerTypes.CONTROLLER, 'AttributeWeatherForm._onSuccess()', {})

          // -- close popin only in case of success
          onCloseDialog()
        }

        dispatchAction(newWeather, id, _onSuccess)
      }
    }))

    Logger.trace(LoggerTypes.CONTAINER, 'AttributeWeatherForm.render()', {
      weather: { id, name, wind_10y_RP, filename, errors, progress, isSuccess }
    })

    return (
      <div ref={ref} className="AttributeForm AttributeWeatherForm">
        <FormTitle title={title} icon={iconNames.WEATHER_DATA} iconSize={20} />
        <form>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Grid container spacing={2} style={{ float: 'left' }}>
                {/*  name  */}
                <Grid item xs={12} sm={12}>
                  <TextField
                    type={textFieldTypes.TEXT}
                    name="name"
                    id="name"
                    value={name}
                    ref={inputRef}
                    variant={variantTypes.OUTLINED}
                    label={t('attributes.weather_data.name.label')}
                    className="common_fullwidth"
                    onChange={onChangeName}
                    placeholder={t('attributes.weather_data.name.placeholder')}
                    error={Boolean(errors && errors?.name)}
                    helperText={(errors && errors?.name) || ''}
                  />
                </Grid>

                {/*  wind_10y_RP  */}
                <Grid item xs={12} sm={12}>
                  <TextField
                    type={textFieldTypes.NUMBER}
                    name="wind_10y_RP"
                    id="wind_10y_RP"
                    label={t('attributes.weather_data.wind_10y_RP.label')}
                    onChange={onChangeWind10yRP}
                    InputProps={{ inputProps: { min: 0.1, max: 99 } }}
                    value={wind_10y_RP}
                    variant={variantTypes.OUTLINED}
                    className="common_fullwidth"
                    placeholder={t('attributes.weather_data.wind_10y_RP.placeholder')}
                    error={Boolean(errors && errors?.wind_10y_RP)}
                    helperText={(errors && errors?.wind_10y_RP) || ''}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Grid container spacing={2} style={{ float: 'left' }}>
                {/*  file upload  */}
                <Grid item xs={12} sm={12} className="common_fullwidth">
                  <Tooltip title={t('attributes.weather_data.file.select.tooltip')}>
                    <>
                      <Button
                        variant={variantTypes.CONTAINED}
                        component="label"
                        className="common_fullwidth"
                        color={errors?.weather_file ? colorTypes.ERROR : colorTypes.PRIMARY}
                      >
                        <Icon name={iconNames.PLUS} size={14} />
                        <span>{filename ?? t('attributes.weather_data.file.select.button')}</span>
                        <input
                          id="btn-upload"
                          name="btn-upload"
                          onChange={onSelectFile}
                          multiple={false}
                          accept=".csv, .xls, .xlsx, text/csv"
                          type="file"
                          hidden
                        />
                      </Button>
                      {errors?.weather_file && (
                        <Typography style={{ fontSize: '0.75rem' }} color={colorTypes.ERROR}>
                          {errors?.weather_file || ''}
                        </Typography>
                      )}
                    </>
                  </Tooltip>

                  {/* <div style={{ margin: '6px 0' }}>{selectedFile ? selectedFile?.name : null}</div> */}

                  <div style={{ borderTop: '1px solid #CCC', marginTop: '15px', paddingTop: '15px' }}>
                    <Grid container spacing={1}>
                      {/*  Date  */}
                      <Grid item xs={12} sm={12}>
                        <b>{t('attributes.weather_data.file.explanation.title')}</b>
                      </Grid>

                      {/*  Date  */}
                      <Grid item xs={2} sm={2}>
                        <b>{t('attributes.weather_data.file.explanation.date.title')}</b>
                      </Grid>
                      <Grid item xs={10} sm={10}>
                        {t('attributes.weather_data.file.explanation.date.description')}
                      </Grid>

                      {/*  Hs  */}
                      <Grid item xs={2} sm={2}>
                        <b>{t('attributes.weather_data.file.explanation.hs.title')}</b>
                      </Grid>
                      <Grid item xs={10} sm={10}>
                        {t('attributes.weather_data.file.explanation.hs.description')}
                      </Grid>

                      {/*  Wind  */}
                      <Grid item xs={2} sm={2}>
                        <b>{t('attributes.weather_data.file.explanation.wind.title')}</b>
                      </Grid>
                      <Grid item xs={10} sm={10}>
                        {t('attributes.weather_data.file.explanation.wind.description')}
                      </Grid>

                      {/*  Current  */}
                      <Grid item xs={2} sm={2}>
                        <b>{t('attributes.weather_data.file.explanation.current.title')}</b>
                      </Grid>
                      <Grid item xs={10} sm={10}>
                        {t('attributes.weather_data.file.explanation.current.description')}
                      </Grid>

                      {/*  Custom  */}
                      <Grid item xs={2} sm={2}>
                        <b>{t('attributes.weather_data.file.explanation.custom.title')}</b>
                      </Grid>
                      <Grid item xs={10} sm={10}>
                        {t('attributes.weather_data.file.explanation.custom.description')}
                      </Grid>
                    </Grid>
                  </div>
                </Grid>

                {/*  Excel sample  */}
                <Grid item xs={12} sm={12} className="common_fullwidth">
                  <Tooltip title={t('attributes.weather_data.file.sample.tooltip')}>
                    <Button
                      variant={variantTypes.OUTLINED}
                      component="span"
                      onClick={onExcelSampleClick}
                      className="common_fullwidth"
                    >
                      <Icon name={iconNames.EXCEL} size={14} />
                      {t('attributes.weather_data.file.sample.button')}
                    </Button>
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>

            {/* -- Uploader progress bar -- */}
            {isSuccess ? (
              <Typography className="success">{t('attributes.weather_data.file.success')}</Typography>
            ) : (
              <LinearProgress
                style={{ height: '5px', borderRadius: '10px', marginTop: '20px' }}
                className="common_fullwidth"
                variant="determinate"
                value={progress}
              />
            )}

            {errors && errors?.generic ? (
              <div className="MuiFormHelperText-root Mui-error MuiFormHelperText-sizeMedium MuiFormHelperText-contained errorField">
                {errors.generic}
              </div>
            ) : null}
          </Grid>
        </form>
      </div>
    )
  }
)

AttributeWeatherForm.displayName = 'AttributeWeatherForm'

AttributeWeatherForm.propTypes = {
  setOpenDialogWeather: PropTypes.func.isRequired,
  dispatchAction: PropTypes.func.isRequired,
  title: PropTypes.string,
  weather: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    wind_10y_RP: PropTypes.number,
    filename: PropTypes.string
  }).isRequired,
  errors: PropTypes.shape({
    name: PropTypes.any,
    wind_10y_RP: PropTypes.any,
    weather_file: PropTypes.any,
    generic: PropTypes.any
  }),
  _progress: PropTypes.number,
  _isSuccess: PropTypes.bool
}

AttributeWeatherForm.defaultProps = {
  setOpenDialogWeather: () => {},
  dispatchAction: () => {},
  title: null,
  weather: defaultValues,
  errors: null,
  _progress: null,
  _isSuccess: false
}
